import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Politique de confidentialité de l'APPLICATION Sanaa Connect.
 *
 * URL publique : https://gssanaa.com/sanaa-connect/politique-confidentialite
 * C'est l'URL déclarée dans les fiches App Store et Play Store. Les deux exigent
 * une politique atteignable SANS authentification, et la revue Apple la lit.
 *
 * ⚠️ PAGE DISTINCTE DE /politique-confidentialite, ET CE N'EST PAS UN DOUBLON.
 * La politique du SITE affirme « Nous ne collectons jamais : … données de santé ».
 * C'est vrai du site, et ce serait FAUX de l'application : un justificatif d'absence
 * est un certificat médical. Étendre la page existante à l'app aurait donc rendu
 * mensongère une phrase déjà publiée. Les deux périmètres restent séparés et se
 * renvoient l'un à l'autre.
 *
 * L'adresse, le téléphone et l'e-mail sont repris TELS QUELS de la page du site.
 *
 * ⚠️ LA RAISON SOCIALE, ELLE, DIVERGE — ET C'EST DÉLIBÉRÉ, PAS UN OUBLI.
 * Cette page nomme le responsable du traitement « Institution Sanaa » (dénomination
 * donnée par le porteur le 13/08/2026). La page du SITE, publiée depuis le 27/04/2026,
 * nomme « Groupe Scolaire Sanaa ». Deux pages du même domaine désignent donc deux
 * responsables de traitement différents, ce qu'un dépôt CNDP expose immédiatement.
 * La page du site n'a PAS été modifiée ici : changer le responsable déclaré d'un
 * document légal déjà publié n'est pas un geste d'alignement rédactionnel.
 * À réconcilier avec l'école avant le dépôt — cf. §14 q14 de la spec technique.
 */

type Lang = 'fr' | 'ar';

const H2 = ({ children }: { children: React.ReactNode }) => (
  <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mt-10 mb-3">{children}</h2>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <p className="text-base leading-relaxed mb-6">{children}</p>
);

const UL = ({ children }: { children: React.ReactNode }) => (
  <ul className="list-disc ps-6 space-y-2 mb-6 text-base leading-relaxed">{children}</ul>
);

/* Encadré des données de santé : le fond rouge pâle n'est pas décoratif, il signale
   le seul régime de traitement plus strict que le reste de la page. */
const Sensitive = ({ children }: { children: React.ReactNode }) => (
  <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6 text-red-900">{children}</div>
);

/* AUCUNE MENTION DE LA CNDP DANS CETTE PAGE, ET C'EST DÉLIBÉRÉ (décision porteur, 14/08/2026).
   La formulation d'origine annonçait « Traitement déclaré auprès de la CNDP sous le numéro … » avec
   le numéro en marqueur à compléter. Retirer le seul marqueur aurait laissé la page AFFIRMER une
   déclaration qui n'a jamais été déposée — une phrase fausse dans un document légal, c'est-à-dire
   pire que le trou qu'elle bouchait. La page se contente donc de dire que le traitement est régi
   par la loi 09-08, ce qui est vrai indépendamment de toute formalité.
   À REPOSER, en FR et en AR, le jour où le dépôt est fait : cf. §14 q14 de la spec technique. */

const Ltr = ({ children }: { children: React.ReactNode }) => (
  <span dir="ltr" className="inline-block">{children}</span>
);

const PolitiqueConfidentialiteAppPage = () => {
  const [lang, setLang] = useState<Lang>('fr');
  const ar = lang === 'ar';

  return (
    <div className="bg-white">
      <div className="container mx-auto px-4 py-12 md:py-20">
        <div
          className="max-w-3xl mx-auto text-gray-800"
          dir={ar ? 'rtl' : 'ltr'}
          lang={lang}
        >
          <div className="flex gap-2 mb-8">
            {(['fr', 'ar'] as Lang[]).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                /* min-h-11 = 44 px : cible tactile minimale, la page est lue au téléphone */
                className={`min-h-11 px-5 rounded-full border text-base font-medium transition ${
                  lang === l
                    ? 'bg-indigo-50 border-indigo-500 text-indigo-900'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {l === 'fr' ? 'Français' : 'العربية'}
              </button>
            ))}
          </div>

          {ar ? (
            /* ─────────────────────────── العربية ─────────────────────────── */
            <>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                سياسة الخصوصية — تطبيق «سناء كونكت»
              </h1>
              <p className="text-sm text-gray-500 mb-8">
                <strong>آخر تحديث: <Ltr>13/08/2026</Ltr></strong>
              </p>

              <P>
                «سناء كونكت» هو التطبيق الذي يربط مؤسسة سناء بأسر تلاميذها: إعلانات
                المدرسة، الواجبات ودفتر النصوص، الغيابات في الوقت الفعلي، والمراسلة مع
                المؤسسة. توضح هذه الصفحة ما هي المعطيات التي يعالجها التطبيق، ولماذا، ومن
                يطّلع عليها، وما هي حقوقكم.
              </P>
              <P>
                تخص هذه السياسة <strong>التطبيق</strong> وحده. أما موقع المدرسة فله{' '}
                <Link to="/politique-confidentialite" className="underline hover:text-gray-600">
                  سياسته الخاصة
                </Link>
                .
              </P>

              <H2>١. المسؤول عن المعالجة</H2>
              <P>
                مؤسسة سناء
                <br />
                <Ltr>364 boulevard Oum Rabia, Oulfa, Casablanca, 20220, Maroc</Ltr>
                <br />
                الهاتف: <Ltr><a href="tel:0522908238" className="underline">0522908238</a></Ltr>
                <br />
                البريد:{' '}
                <Ltr><a href="mailto:info@gssanaa.com" className="underline">info@gssanaa.com</a></Ltr>
              </P>
              <P>
                يطوّر التطبيق ويشغّله لحساب المدرسة <strong>Casarad</strong>، بصفته معالجاً من
                الباطن فقط، ولا يستعمل هذه المعطيات لأي غاية خاصة به. وتخضع هذه المعالجة{' '}
                <strong>للقانون <Ltr>09-08</Ltr></strong> المتعلق بحماية الأشخاص الذاتيين تجاه
                معالجة المعطيات ذات الطابع الشخصي.
              </P>

              <H2>٢. ما هي المعطيات ولماذا</H2>
              <UL>
                <li><strong>الاسم والنسب، البريد الإلكتروني، رقم الهاتف</strong> — إنشاء حسابكم، ربطكم بابنكم، وإرسال رمز التحقق.</li>
                <li><strong>علاقة النسب والمسار الدراسي</strong> (القسم، المستوى، السنة) — ألا يُعرض عليكم إلا ما يخص أبناءكم.</li>
                <li><strong>الغيابات، التأخرات، الواجبات، دفتر النصوص، الفروض، الإعلانات</strong> — جوهر الخدمة.</li>
                <li><strong>الرسائل والشكايات ومرفقاتها</strong> — التواصل مع المؤسسة وتتبع طلبكم.</li>
                <li><strong>وثائق تبرير الغياب</strong> — انظروا الفقرة ٣: معالجتها خاصة.</li>
                <li><strong>جدولة أداء الرسوم</strong> — تُقرأ مباشرة من نظام تدبير المدرسة ولا تُنسخ داخل التطبيق.</li>
                <li><strong>معرّف الإشعارات وطراز الجهاز ولغته</strong> — إرسال الإشعارات، والتوقف عن إرسالها لجهاز لم تعودوا تستعملونه.</li>
                <li><strong>السجلات التقنية</strong> — كشف الأعطاب. وهي منقّاة: لا اسم ولا هاتف ولا مضمون رسالة.</li>
              </UL>
              <P>
                التطبيق <strong>لا يجمع</strong> موقعكم الجغرافي، ولا دفتر عناوينكم، ولا
                الميكروفون، ولا أي معرّف إشهاري. ولا يُطلب الولوج إلى صوركم أو الكاميرا إلا
                لحظة إرفاقكم صورة بأنفسكم، ولا تُرسل إلا الصورة التي تختارونها.
              </P>

              <H2>٣. وثائق تبرير الغياب تُعامَل معاملة المعطيات الصحية</H2>
              <Sensitive>
                <p className="mb-3">الشهادة الطبية تكشف الحالة الصحية لطفل. لذلك فإن هذه الملفات:</p>
                <ul className="list-disc ps-6 space-y-2 mb-3">
                  <li><strong>مشفّرة قبل إيداعها</strong> على الخوادم؛</li>
                  <li>لا تُقدَّم إلا عبر طلب موثّق عند كل اطّلاع — فلا وجود لرابط تحميل يمكن تمريره أو تخمينه؛</li>
                  <li><strong>غير مرئية للطرف الآخر من الأبوين</strong>، ولو كان مرتبطاً بالطفل نفسه؛</li>
                  <li>لا يطّلع عليها إلا الموظفون المؤهلون، و<strong>كل اطّلاع يُسجَّل</strong>.</li>
                </ul>
                <p className="mb-0">
                  إيداع الوثيقة يخضع لموافقة مستقلة تُجمع عند الإيداع. ولستم مُلزمين بالمرور عبر
                  التطبيق لتبرير غياب.
                </p>
              </Sensitive>

              <H2>٤. معطيات ابنكم</H2>
              <P>
                يعالج التطبيق معطيات تلاميذ قاصرين، مصدرها الملف المدرسي الذي تمسكه المدرسة؛
                التطبيق لا ينشئها بل يعرضها على المؤهلين. ولا يلج أحد الأبوين ملف طفل إلا بعد
                تحقق برمز يُرسل إلى <strong>رقم الهاتف المسجَّل في ملف التلميذ</strong>، وفي
                الحدود التي تضعها المدرسة. وفي حالة الانفصال أو حكم قضائي، يمكن للمؤسسة تقييد
                ولوج أحد الأبوين؛ وهذه التقييدات مؤرخة ومتتبَّعة.
              </P>
              <P>
                الإشعارات لا تتضمن أبداً نسب الطفل، ولا سبب الغياب، ولا مضمون رسالة: فهي مصاغة
                بحيث لا تكشف شاشة مقفلة يطّلع عليها غريب أي أمر حساس.
              </P>

              <H2>٥. من يطّلع على هذه المعطيات</H2>
              <P>لا تبيع المدرسة أي معطى ولا تتقاسمه لغاية تجارية. ويعتمد التطبيق على المزوّدين التالين، كلٌّ في حدود ما يلزمه:</P>
              <UL>
                <li><Ltr>Google (Firebase)</Ltr> — الاتصال بالحساب وتخزين الملفات.</li>
                <li><Ltr>Google (Gemini)</Ltr> — ترجمة أولية للإعلانات من جهة المدرسة، <strong>دون أي محتوى اسمي</strong>، ولا تُستعمل هذه النصوص لتدريب النموذج.</li>
                <li><Ltr>Apple</Ltr> — «تسجيل الدخول بحساب Apple» والإشعارات على <Ltr>iPhone</Ltr>.</li>
                <li><Ltr>Expo</Ltr> — توجيه الإشعارات.</li>
                <li><Ltr>Meta (WhatsApp)</Ltr> و<Ltr>Vonage</Ltr> — إرسال رمز التحقق عبر واتساب ثم عبر <Ltr>SMS</Ltr> عند التعذّر.</li>
                <li><Ltr>Sentry</Ltr> — تقارير أعطاب منقّاة من كل معطى شخصي.</li>
              </UL>
              <P>
                يستضيف هؤلاء كل المعطيات أو بعضها <strong>خارج المغرب</strong>، ويخضع هذا النقل
                لإجراءات القانون <Ltr>09-08</Ltr> ولالتزامات تعاقدية بالحماية. ورمز التحقق لا
                يُحفظ أبداً بشكل مقروء ولا يظهر في أي سجل تقني.
              </P>

              <H2>٦. مدة الاحتفاظ</H2>
              <UL>
                <li><strong>الحساب والروابط</strong> — ما دام ابنكم مسجلاً، ثم وفق التزامات الأرشفة المدرسية.</li>
                <li><strong>محتويات الحياة المدرسية</strong> — تنتمي إلى الملف المدرسي وتتبع قواعده.</li>
                <li><strong>رقم الهاتف ورمز التحقق</strong> — يُمحيان بعد <Ltr>7</Ltr> أيام.</li>
                <li><strong>النسخ الاحتياطية المشفّرة</strong> — <Ltr>30</Ltr> يوماً يومياً، و<Ltr>12</Ltr> شهراً شهرياً.</li>
              </UL>

              <H2>٧. حذف حسابكم</H2>
              <P>
                يمكنكم حذف حسابكم <strong>من داخل التطبيق</strong> عبر إعدادات ملفكم الشخصي، دون
                مراسلة أحد، وينبهكم التطبيق إلى ما سيُمحى قبل التنفيذ. ويترتب عليه إلغاء روابطكم،
                وتطهير أجهزتكم ورموز إشعاراتكم، وإخفاء هوية محتوياتكم، وحذف حساب المصادقة. وتتيح
                لكم <strong>مهلة <Ltr>30</Ltr> يوماً</strong> التراجع بمجرد إعادة الاتصال.
              </P>
              <P>
                واستثناءان يفرضهما القانون: النسخ الاحتياطية المنجزة سلفاً تنقضي وفق الجدول أعلاه،
                وسجلات التدقيق واجبة الحفظ مستثناة من المحو. وحذف حساب الولوج لا يمحو الملف
                المدرسي لابنكم، فهو ملك للمدرسة.
              </P>

              <H2>٨. حقوقكم</H2>
              <P>
                طبقاً للقانون <Ltr>09-08</Ltr>، لكم حق الولوج والتصحيح والحذف والتعرض والحد من
                المعالجة، وسحب أي موافقة منحتموها. تُوجَّه الطلبات إلى{' '}
                <Ltr><a href="mailto:info@gssanaa.com" className="underline">info@gssanaa.com</a></Ltr>{' '}
                أو إلى عنوان المسؤول عن المعالجة، ونجيب داخل أجل أقصاه <Ltr>30</Ltr> يوماً.
                ويمكنكم كذلك تقديم شكاية لدى <Ltr>CNDP</Ltr>.
              </P>

              <H2>٩. الأمن</H2>
              <P>
                المبادلات مشفّرة بواسطة <Ltr>TLS</Ltr>. والمعطيات المخزّنة مؤقتاً على هاتفكم
                مشفّرة بمفتاح خاص بجهازكم ومستثناة من النسخ الاحتياطي السحابي؛ أما جدولة الأداء
                ووثائق التبرير فلا تُخزَّن على الهاتف إطلاقاً. وتسجيل الخروج يمحو هذه الذاكرة
                بالكامل. وتُتحقَّق صلاحيات الموظفين عند كل طلب، ويُسجَّل كل اطّلاع على معطى حساس.
              </P>

              <H2>١٠. التعديلات</H2>
              <P>
                كل تعديل جوهري سيُعلن داخل التطبيق قبل دخوله حيز التنفيذ، والتاريخ أعلى الصفحة
                يشير إلى النسخة الجاري بها العمل.
              </P>
            </>
          ) : (
            /* ─────────────────────────── FRANÇAIS ─────────────────────────── */
            <>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Politique de confidentialité — application Sanaa Connect
              </h1>
              <p className="text-sm text-gray-500 mb-8">
                <strong>Dernière mise à jour : 13 août 2026</strong>
              </p>

              <P>
                Sanaa Connect est l’application qui relie l’Institution Sanaa aux familles
                de ses élèves : annonces de l’école, devoirs et cahier de textes, absences en
                temps réel, messagerie avec l’établissement. Cette page explique quelles données
                l’application traite, pourquoi, qui y a accès et quels sont vos droits. Elle est
                écrite pour être lue par des parents, pas par des juristes.
              </P>
              <P>
                Elle concerne <strong>l’application seule</strong>. Le site de l’école a sa{' '}
                <Link to="/politique-confidentialite" className="underline hover:text-gray-600">
                  propre politique
                </Link>
                , dont le périmètre est différent.
              </P>

              <H2>1. Responsable du traitement</H2>
              <P>
                Institution Sanaa
                <br />
                364 boulevard Oum Rabia, Oulfa, Casablanca, 20220, Maroc
                <br />
                Téléphone :{' '}
                <a href="tel:0522908238" className="underline hover:text-gray-600">0522908238</a>
                <br />
                Email :{' '}
                <a href="mailto:info@gssanaa.com" className="underline hover:text-gray-600">info@gssanaa.com</a>
              </P>
              <P>
                L’application est développée et exploitée pour le compte de l’école par{' '}
                <strong>Casarad</strong>, qui agit uniquement comme <strong>sous-traitant</strong>{' '}
                et n’utilise ces données pour aucune finalité propre. Le traitement est régi par la{' '}
                <strong>loi 09-08</strong> relative à la protection des personnes physiques à
                l’égard du traitement des données à caractère personnel.
              </P>

              <H2>2. Quelles données et pourquoi</H2>
              <UL>
                <li><strong>Nom, prénom, e-mail, numéro de téléphone mobile</strong> — créer votre compte, vous rattacher à votre enfant, vous envoyer le code de vérification.</li>
                <li><strong>Lien avec votre enfant et sa scolarité</strong> (classe, niveau, année) — ne vous montrer que ce qui concerne vos propres enfants.</li>
                <li><strong>Absences, retards, devoirs, cahier de textes, contrôles, annonces</strong> — le cœur du service.</li>
                <li><strong>Messages et réclamations</strong> et leurs pièces jointes — échanger avec l’établissement et suivre votre demande.</li>
                <li><strong>Justificatifs d’absence</strong> — voir la section 3 : leur traitement est particulier.</li>
                <li><strong>Échéancier de scolarité</strong> — lu en direct dans le système de gestion de l’école, jamais recopié dans l’application.</li>
                <li><strong>Identifiant de notification de l’appareil, modèle et langue</strong> — vous envoyer les notifications, et cesser d’en envoyer à un appareil que vous n’utilisez plus.</li>
                <li><strong>Journaux techniques et rapports d’erreur</strong> — détecter pannes et accès abusifs. Ils sont expurgés : ni nom, ni téléphone, ni contenu de message.</li>
              </UL>
              <P>
                L’application <strong>ne collecte pas</strong> votre position, votre carnet
                d’adresses, votre microphone, ni aucun identifiant publicitaire. L’accès à vos
                photos et à l’appareil photo n’est demandé qu’au moment où vous joignez vous-même
                une image, et seule l’image que vous choisissez est transmise.
              </P>

              <H2>3. Les justificatifs d’absence sont traités comme des données de santé</H2>
              <Sensitive>
                <p className="mb-3">
                  Un certificat médical révèle l’état de santé d’un enfant. Ces fichiers sont donc :
                </p>
                <ul className="list-disc ps-6 space-y-2 mb-3">
                  <li><strong>chiffrés avant même d’être déposés</strong> sur nos serveurs ;</li>
                  <li>servis uniquement à travers une requête authentifiée, à chaque consultation — il n’existe aucun lien de téléchargement qu’on pourrait transférer ou deviner ;</li>
                  <li><strong>invisibles pour l’autre parent</strong>, y compris s’il est lié au même enfant ;</li>
                  <li>consultables uniquement par le personnel habilité, et <strong>chaque consultation est journalisée</strong>.</li>
                </ul>
                <p className="mb-0">
                  Le dépôt d’un justificatif fait l’objet d’un consentement distinct, recueilli au
                  moment du dépôt. Vous n’êtes jamais obligé de passer par l’application pour
                  justifier une absence.
                </p>
              </Sensitive>

              <H2>4. Les données de votre enfant</H2>
              <P>
                L’application traite les données d’élèves mineurs. Elles proviennent du dossier
                scolaire tenu par l’école ; l’application ne les crée pas, elle les présente aux
                personnes autorisées. Un parent n’accède au dossier d’un enfant qu’après une
                vérification par code envoyé au <strong>numéro de téléphone enregistré dans le
                dossier de l’élève</strong>, et dans les limites fixées par l’école. En cas de
                séparation ou de décision de justice, l’établissement peut restreindre ou
                suspendre l’accès d’un parent ; ces restrictions sont datées et tracées.
              </P>
              <P>
                Les notifications envoyées sur votre téléphone ne contiennent jamais le nom de
                famille de l’enfant, ni le motif d’une absence, ni le contenu d’un message :
                elles sont écrites pour qu’un écran verrouillé consulté par un tiers ne révèle
                rien de sensible.
              </P>

              <H2>5. Qui d’autre voit ces données</H2>
              <P>
                L’école ne vend aucune donnée et ne les partage à aucune fin commerciale. Pour
                fonctionner, l’application s’appuie sur les prestataires suivants, chacun limité
                à ce qui lui est strictement nécessaire :
              </P>
              <UL>
                <li><strong>Google (Firebase)</strong> — connexion au compte et stockage des fichiers.</li>
                <li><strong>Google (Gemini)</strong> — pré-traduction français ↔ arabe des annonces, côté école, <strong>jamais avec un contenu nominatif</strong> ; ces textes ne servent pas à entraîner le modèle.</li>
                <li><strong>Apple</strong> — « Se connecter avec Apple » et notifications sur iPhone.</li>
                <li><strong>Expo</strong> — acheminement des notifications.</li>
                <li><strong>Meta (WhatsApp)</strong> et <strong>Vonage</strong> — envoi du code de vérification par WhatsApp, puis par SMS en cas d’échec.</li>
                <li><strong>Sentry</strong> — rapports d’erreur expurgés de toute donnée personnelle.</li>
              </UL>
              <P>
                Ces prestataires hébergent tout ou partie des données <strong>hors du Maroc</strong>.
                Ce transfert fait l’objet des formalités prévues par la loi 09-08 et d’engagements
                contractuels de protection. Le code de vérification que vous recevez n’est jamais
                conservé en clair et n’apparaît dans aucun journal technique.
              </P>

              <H2>6. Combien de temps les données sont conservées</H2>
              <UL>
                <li><strong>Compte et liaisons</strong> — tant que votre enfant est inscrit, puis selon les obligations d’archivage scolaire.</li>
                <li><strong>Contenus de la vie scolaire</strong> — ils appartiennent au dossier scolaire et suivent ses règles.</li>
                <li><strong>Numéro de téléphone et code de vérification</strong> — effacés 7 jours après la demande.</li>
                <li><strong>Sauvegardes chiffrées</strong> — 30 jours en quotidien, 12 mois en mensuel.</li>
              </UL>

              <H2>7. Supprimer votre compte</H2>
              <P>
                Vous pouvez supprimer votre compte <strong>depuis l’application elle-même</strong>,
                dans les réglages de votre profil, sans écrire à personne. L’application vous
                prévient de ce qui va être effacé <em>avant</em> le geste. La suppression entraîne
                la révocation de vos liaisons, la purge de vos appareils et jetons de notification,
                l’anonymisation des contenus que vous avez envoyés et la suppression de votre
                compte d’authentification. Un <strong>délai de grâce de 30 jours</strong> vous
                permet de revenir sur votre décision en vous reconnectant simplement.
              </P>
              <P>
                Deux exceptions, que la loi impose : les sauvegardes chiffrées déjà constituées
                expirent d’elles-mêmes selon le calendrier ci-dessus, et les journaux d’audit à
                conservation obligatoire sont soustraits à l’effacement. Supprimer votre compte
                d’accès n’efface pas le dossier scolaire de votre enfant, qui appartient à l’école.
              </P>

              <H2>8. Vos droits</H2>
              <P>
                Conformément à la loi 09-08, vous disposez des droits d’accès, de rectification,
                de suppression, d’opposition et de limitation du traitement, ainsi que du droit de
                retirer un consentement que vous avez donné. Ces demandes s’adressent à{' '}
                <a href="mailto:info@gssanaa.com" className="underline hover:text-gray-600">info@gssanaa.com</a>{' '}
                ou par courrier à l’adresse du responsable du traitement (§1) ; nous répondons
                sous 30 jours maximum. Vous pouvez également introduire une réclamation auprès de
                la CNDP.
              </P>

              <H2>9. Sécurité</H2>
              <P>
                Les échanges entre l’application et nos serveurs sont chiffrés par TLS. Les données
                mises en cache sur votre téléphone sont chiffrées avec une clé propre à votre
                appareil et exclues des sauvegardes automatiques vers le cloud ; l’échéancier et
                les justificatifs ne sont jamais stockés sur le téléphone. Se déconnecter efface
                entièrement ce cache. Les accès du personnel sont vérifiés à chaque requête, et
                toute consultation de donnée sensible est journalisée.
              </P>

              <H2>10. Modifications</H2>
              <P>
                Toute modification substantielle de cette politique sera annoncée dans
                l’application avant son entrée en vigueur. La date en tête de page indique la
                version en cours.
              </P>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PolitiqueConfidentialiteAppPage;
